import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useFormikContext } from "formik";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import useWallet from "../../../../hooks/useWallet";
import { Address as AddressClass } from "../../../../lib/address/Address";
import ManualAddressInput from "./ManualAddressInput";
import ConnectWalletButton from "../../../../components/Common/ConnectWalletButton";
import AddressBook from "./AddressBook";
import AddressButton from "./AddressButton";
import { useInitialSettings } from "../../../../context/settings";
import ConnectedWallets, { NotCompatibleWallets } from "./ConnectedWallets";
import VaulDrawer from "../../../../components/Modal/vaulModal";
import { useManualDestAddresses, useSelectedAccount, useSelectSwapAccount } from "../../../../context/swapAccounts";
import { useManualDestAddressesStore } from "../../../../stores/manualDestAddressesStore";
import { savedAddressMatchesNetwork, useAddressBookStore } from "../../../../stores/addressBookStore";
export var AddressGroup;
(function (AddressGroup) {
    AddressGroup["ConnectedWallet"] = "Connected wallet";
    AddressGroup["ManualAdded"] = "Added Manually";
    AddressGroup["FromQuery"] = "Partner";
})(AddressGroup || (AddressGroup = {}));
const AddressPicker = forwardRef(function Address({ showAddressModal, setShowAddressModal, name, canFocus, close, partner, children, inline, disableAutoFill }, ref) {
    const { values, setFieldValue } = useFormikContext();
    const initialSettings = useInitialSettings();
    const { destination_address, to: destination, toExchange } = values;
    const selectDestinationAccount = useSelectSwapAccount("to");
    const { provider, unAvailableWallets } = useWallet(destination, 'autofill');
    const connectedWallets = provider?.connectedWallets?.filter(w => !w.isNotAvailable) || [];
    const defaultAccount = useSelectedAccount("to", values.to?.name);
    const connectedWalletskey = connectedWallets?.map(w => w.addresses.join('')).join('');
    const [manualAddress, setManualAddress] = useState('');
    const [isConnecting, setIsConnecting] = useState(false);
    const savedAddresses = useAddressBookStore(s => s.savedAddresses);
    const manualDestAddresses = useManualDestAddresses();
    const removeManualDestAddress = useManualDestAddressesStore(s => s.removeManualDestAddress);
    const removeAddressFromBook = useAddressBookStore(s => s.removeAddress);
    const onRemoveAddress = useCallback((address, isBookEntry) => {
        if (isBookEntry) {
            removeAddressFromBook(address);
        }
        if (provider?.name) {
            removeManualDestAddress(address, provider.name);
        }
    }, [removeAddressFromBook, provider?.name, removeManualDestAddress]);
    useEffect(() => {
        if (destination_address && destination && !AddressClass.isValid(destination_address, destination)) {
            updateDestAddress('');
            setManualAddress('');
        }
    }, [destination, destination_address]);
    const inputReference = useRef(null);
    const groupedAddresses = useMemo(() => {
        return resolveAddressGroups({
            destination,
            wallets: connectedWallets,
            savedAddresses,
            manualAddresses: manualDestAddresses,
            addressFromQuery: initialSettings.destination_address,
            providerName: provider?.name,
        });
    }, [destination, connectedWallets, savedAddresses, manualDestAddresses, initialSettings.destination_address, connectedWalletskey, provider?.name]);
    const destinationAddressItem = destination && destination_address ?
        groupedAddresses?.find(a => a.address.toLowerCase() === destination_address.toLowerCase())
        : undefined;
    const addressBookAddresses = groupedAddresses?.filter(a => a.group !== AddressGroup.ConnectedWallet);
    const incompatibleAddressBook = useMemo(() => destination
        ? savedAddresses
            .filter(e => !(savedAddressMatchesNetwork(e, destination) && AddressClass.isValid(e.address, destination)))
            .map(e => ({ address: e.address, group: AddressGroup.ManualAdded, providerName: e.networkTypes?.[0] }))
        : [], [savedAddresses, destination]);
    const normalizedDestAddress = useMemo(() => destination && destination_address
        ? new AddressClass(destination_address, destination).normalized
        : null, [destination_address, destination]);
    const connectedWallet = (destination && normalizedDestAddress)
        ? connectedWallets?.find(w => w.addresses?.some(a => new AddressClass(a, destination).normalized === normalizedDestAddress))
        : undefined;
    const handleSelectAddress = useCallback((address) => {
        const selected = destination && groupedAddresses?.find(a => AddressClass.equals(a.address, address, destination));
        const formattedAddress = selected?.address;
        updateDestAddress(formattedAddress);
        close();
    }, [close, setFieldValue, groupedAddresses]);
    const onConnect = (wallet) => {
        setFieldValue('destination_address', wallet.address);
        selectDestinationAccount({
            address: wallet.address,
            id: wallet.id,
            providerName: wallet.providerName
        });
        close();
    };
    useEffect(() => {
        if (disableAutoFill)
            return;
        if (destinationAddressItem && !defaultAccount?.address && destinationAddressItem?.group == AddressGroup.ConnectedWallet) {
            updateDestAddress(undefined);
            return;
        }
        if (destination_address?.toLowerCase() !== defaultAccount?.address?.toLowerCase() && (!destinationAddressItem || destinationAddressItem?.group === AddressGroup.ConnectedWallet)) {
            updateDestAddress(defaultAccount?.address);
            setShowAddressModal(false);
        }
    }, [defaultAccount?.address, destinationAddressItem, disableAutoFill]);
    const updateDestAddress = useCallback((address) => {
        const wallet = destination && connectedWallets?.find(w => w.addresses?.some(a => AddressClass.equals(a, address || '', destination)));
        setFieldValue('destination_address', address);
        if (destination && address && provider) {
            if (wallet)
                selectDestinationAccount({
                    address: address,
                    id: wallet.id,
                    providerName: wallet.providerName
                });
            else
                selectDestinationAccount({
                    address: address || "",
                    id: 'manually_added',
                    providerName: provider.name,
                });
        }
    }, [destination, connectedWallets, provider, selectDestinationAccount]);
    useEffect(() => {
        if (canFocus) {
            inputReference?.current?.focus();
        }
    }, [canFocus]);
    const pickerBody = (_jsx("div", { className: 'w-full flex flex-col justify-between h-full text-primary-text', children: _jsxs("div", { className: 'flex flex-col self-center grow w-full space-y-5 h-full', children: [destination
                    && provider
                    && !connectedWallets.length &&
                    _jsx(ConnectWalletButton, { provider: provider, onConnect: onConnect }), _jsx(ManualAddressInput, { manualAddress: manualAddress, setManualAddress: setManualAddress, setNewAddress: (props) => updateDestAddress(props?.address), values: values, partner: partner, name: name, inputReference: inputReference, setFieldValue: setFieldValue, close: close, addresses: groupedAddresses, connectedWallet: connectedWallet }), destination
                    && provider
                    && !manualAddress &&
                    _jsx(ConnectedWallets, { provider: provider, onClick: (props) => handleSelectAddress(props.address), onConnect: onConnect, destination: destination, destination_address: destination_address, isLoading: isConnecting, setIsLoading: setIsConnecting }), addressBookAddresses && addressBookAddresses?.length > 0 && !manualAddress && destination &&
                    _jsx(AddressBook, { addressBook: addressBookAddresses, onSelectAddress: handleSelectAddress, destination: destination, destination_address: destination_address, partner: partner, onRemove: onRemoveAddress }), destination && !manualAddress && (unAvailableWallets.length > 0 || incompatibleAddressBook.length > 0) &&
                    _jsx(NotCompatibleWallets, { notCompatibleWallets: unAvailableWallets, notCompatibleAddresses: incompatibleAddressBook, destination: destination, partner: partner, isLoading: isConnecting })] }) }));
    if (inline) {
        return pickerBody;
    }
    return (_jsxs(_Fragment, { children: [_jsx(AddressButton, { addressItem: destinationAddressItem, openAddressModal: () => setShowAddressModal(true), connectedWallet: connectedWallet, partner: partner, destination: destination, children: children ? children({ destination, addressItem: destinationAddressItem, connectedWallet: connectedWallet, partner }) : _jsx(_Fragment, {}) }), _jsx(VaulDrawer, { header: 'Send To', show: showAddressModal, setShow: setShowAddressModal, modalId: "address", children: _jsx(VaulDrawer.Snap, { id: "item-1", children: pickerBody }) })] }));
});
const resolveAddressGroups = ({ destination, wallets, savedAddresses, manualAddresses, addressFromQuery, providerName, }) => {
    if (!destination)
        return;
    const addresses = [];
    wallets?.forEach(wallet => {
        if (wallet?.addresses?.length) {
            addresses.push(...(wallet.addresses.map(a => ({ address: a, group: AddressGroup.ConnectedWallet, wallet })) || []));
        }
    });
    if (addressFromQuery && AddressClass.isValid(addressFromQuery, destination)) {
        addresses.push({ address: addressFromQuery, group: AddressGroup.FromQuery });
    }
    manualAddresses.forEach(entry => {
        if (entry.providerName !== providerName || !AddressClass.isValid(entry.address, destination))
            return;
        const bookEntry = savedAddresses.find(e => AddressClass.equals(e.address, entry.address, destination, providerName));
        if (bookEntry && !savedAddressMatchesNetwork(bookEntry, destination))
            return;
        addresses.push({
            address: entry.address,
            group: AddressGroup.ManualAdded,
            providerName: entry.providerName,
        });
    });
    savedAddresses.forEach(entry => {
        if (savedAddressMatchesNetwork(entry, destination) && AddressClass.isValid(entry.address, destination)) {
            addresses.push({
                address: entry.address,
                group: AddressGroup.ManualAdded,
                providerName,
            });
        }
    });
    const uniqueAddresses = getUniqueAddresses(addresses, destination);
    return uniqueAddresses;
};
const getUniqueAddresses = (addresses, destination) => {
    const normalizedMap = new Map();
    addresses.forEach((a) => {
        const normalized = new AddressClass(a.address, destination).normalized;
        if (!normalizedMap.has(normalized)) {
            normalizedMap.set(normalized, a);
        }
    });
    return Array.from(normalizedMap.values());
};
export default AddressPicker;
